import { formattedUserFacingDateTime } from 'DateHelpers';
import { RenderMarkdown } from 'MarkdownComponents';
import { Fragment, useState } from 'react';
import { type HistoricalProjectSubmission, type TimelineChange } from './ProjectFieldsModal.types';
import { useProjectTimeline } from './useProjectTimeline';

const EXPANDABLE_TEXT_FIELDS = new Set([
    'Feedback',
    'Provisional Feedback',
    'Private Notes',
    'Presentation Transcript',
]);

// A session-status line always goes from BLANK to a terminal value. Each session reaches exactly one
// terminal state, and a retry is a separate session. The "from BLANK" part is therefore constant
// noise. Render only "to <value>" for these lines (#17103).
const STATUS_REACHED_FIELDS = new Set(['Presentation Transcript Status', 'Auto-Grading Status']);

type ProjectChangeHistoryProps = {
    projectProgressId: string | null;
    updatedAt: number | null;
    historicalSubmissions: HistoricalProjectSubmission[];
};

function isExpandable(change: TimelineChange): boolean {
    return EXPANDABLE_TEXT_FIELDS.has(change.attr);
}

function isStatusReached(change: TimelineChange): boolean {
    return STATUS_REACHED_FIELDS.has(change.attr);
}

function isExpandableChange(change: TimelineChange): boolean {
    return isExpandable(change) && change.to !== null;
}

// The React port of the project-progress-events directive, "Change History". It renders the
// timeline. A long text-field change expands to markdown on demand.
export function ProjectChangeHistory({
    projectProgressId,
    updatedAt,
    historicalSubmissions,
}: ProjectChangeHistoryProps) {
    const events = useProjectTimeline(projectProgressId, updatedAt);
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});

    const sortedSubmissions = [...historicalSubmissions].sort((a, b) => a.created_at - b.created_at);

    // A submission's number is its position in the history, ordered by date. The id stays in
    // historicalSubmissions even after the link breaks. So this resolves a link and an unlink
    // alike (#17085).
    const submissionLabel = (submissionId: string | null): string => {
        const index = sortedSubmissions.findIndex(submission => submission.id === submissionId);
        return index >= 0 ? `Submission #${index + 1}` : 'a submission';
    };

    const formatChangeLabel = (change: TimelineChange): string => {
        if (change.attr === 'Project Submission') {
            // Linking to a submission.
            if (change.to !== null) {
                return ` created ${submissionLabel(change.to)}`;
            }
            // An unlink sets project_submission_id to BLANK. One path breaks the link in practice:
            // ProjectGroupMembership#sever_project_submission_link! runs when someone removes the
            // learner from their project group. The message names that cause, but hedges. A
            // submission destroy would also null the link, though nothing triggers one today
            // (#17085).
            return ` unlinked from ${submissionLabel(change.from)} (typically indicates removal from group)`;
        }
        if (isStatusReached(change)) {
            return ` changed ${change.attr} to ${change.to === null ? 'BLANK' : change.to}`;
        }
        if (isExpandable(change)) {
            return ` changed ${change.attr}${change.to === null ? ' to BLANK' : ''}`;
        }
        return ` changed ${change.attr} from ${change.from === null ? 'BLANK' : change.from} to ${
            change.to === null ? 'BLANK' : change.to
        }`;
    };

    const toggle = (key: string) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

    return (
        <div>
            <h4>Change History</h4>
            {events.length === 0 ? (
                <p className="text-sm text-slate-grey">No history detected</p>
            ) : (
                <ul className="list-none space-y-2 p-0">
                    {events.map(event => {
                        // The server can emit several events with one version_id, one per changed
                        // attribute. The version_id alone is not unique, so add the attribute names.
                        const eventKey = `${event.version_id}-${event.changes.map(change => change.attr).join('-')}`;
                        // An abnormal grading-pipeline outcome shows in bold orange with a ⚠ icon.
                        // The cue does not depend on color alone (#17103).
                        const isWarning = event.level === 'warning';
                        return (
                            <li key={eventKey} className={isWarning ? 'font-bold text-[#b3541e]' : undefined}>
                                {/* font-normal so the timestamp never inherits a warning row's bold. */}
                                <span className="me-1 text-xs font-normal text-slate-grey">
                                    {formattedUserFacingDateTime(new Date(event.time * 1000))}
                                </span>
                                {isWarning && (
                                    <span className="me-1 text-[#b3541e]" title="Aberrant grading-pipeline outcome">
                                        ⚠
                                    </span>
                                )}
                                {event.labels?.[0]?.includes('Submission') && (
                                    <span className="me-1">{event.labels.join(', ')} –</span>
                                )}
                                <span className="font-semibold">{event.editor_name}</span>
                                {event.changes.map((change, index) => {
                                    const expandKey = `${eventKey}-${change.attr}`;
                                    return (
                                        <Fragment key={expandKey}>
                                            <span>{formatChangeLabel(change)}</span>
                                            {isExpandableChange(change) && (
                                                <button
                                                    type="button"
                                                    className="mx-1 border-0 bg-transparent p-0 text-blue"
                                                    onClick={() => toggle(expandKey)}
                                                >
                                                    {expanded[expandKey] ? '▾' : '▸'}
                                                </button>
                                            )}
                                            {index < event.changes.length - 1 && <span>;&nbsp;</span>}
                                            {isExpandableChange(change) && expanded[expandKey] && (
                                                <RenderMarkdown className="mt-1 rounded-5 bg-slate-grey-50 p-2 text-sm">
                                                    {change.to ?? ''}
                                                </RenderMarkdown>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
